<template>
  <aside
    class="absolute left-0 top-0 z-10 h-screen flex-shrink-0 select-none border-x bg-surface-0 border-surface dark:bg-surface-950"
    :class="[isExpanded ? 'w-[275px] min-[1920px]:w-[350px]' : 'w-[75px]']"
  >
    <div class="group relative flex h-full flex-col">
      <div class="flex items-center justify-between p-4">
        <MusthadFullLogo v-if="isExpanded" class="w-40" />
        <MusthadLogo
          v-else
          class="mx-auto w-8 transition-opacity duration-300 group-hover:opacity-0"
        />

        <div
          class="hidden xl:block"
          :class="[
            isExpanded
              ? ''
              : 'absolute left-5 top-4 opacity-0 transition-opacity duration-300 group-hover:opacity-100',
          ]"
        >
          <OverlayBadge
            :value="0"
            severity="danger"
            size="small"
            :pt="{
              pcBadge: {
                root: 'hidden',
              },
            }"
          >
            <Button
              @click="isExpanded = !isExpanded"
              size="small"
              variant="outlined"
              severity="secondary"
              aria-label="Sidebar"
              class="bg-white p-1.5"
            >
              <RiSidebarFoldLine :class="isExpanded ? '' : 'rotate-180'" />
            </Button>
          </OverlayBadge>
        </div>
      </div>

      <div class="overflow-y-auto">
        <ul class="list-none space-y-1.5 overflow-hidden p-4">
          <li v-for="sidebarLink in SIDEBAR_LINKS" :key="sidebarLink.label">
            <button
              @click="navigateTo(sidebarLink.link)"
              class="group/button flex w-full items-center rounded-md p-2 transition-colors focus-visible:outline-none focus-visible:ring focus-visible:ring-surface-200 hover:enabled:bg-surface-50 disabled:cursor-not-allowed"
              :class="[
                isLinkActive(sidebarLink) ? 'bg-surface-50' : '',
                isExpanded ? 'justify-between' : 'justify-center',
              ]"
              :disabled="sidebarLink.soon || sidebarLink.limited"
              v-tooltip.right="getNavigationLinkTooltip(sidebarLink)"
            >
              <span class="flex items-center gap-3">
                <component
                  :is="sidebarLink.icon"
                  class="size-5 group-disabled/button:opacity-75"
                  :class="[
                    isLinkActive(sidebarLink)
                      ? 'text-primary'
                      : 'text-surface-600',
                  ]"
                />
                <span
                  v-if="isExpanded"
                  class="font-medium group-disabled/button:opacity-75"
                  :class="[
                    isLinkActive(sidebarLink)
                      ? 'text-surface-950'
                      : 'text-surface-600',
                  ]"
                >
                  {{ sidebarLink.label }}
                </span>
                <template v-if="isExpanded">
                  <Tag
                    v-if="sidebarLink.soon"
                    severity="secondary"
                    size="small"
                    :value="t('global.soon')"
                  />
                  <Tag
                    v-else-if="sidebarLink.limited"
                    severity="secondary"
                    size="small"
                    :value="t('global.limited-access')"
                    class="opacity-0 transition-opacity duration-300 group-hover/button:opacity-100"
                  />
                </template>
              </span>

              <Badge
                v-if="sidebarLink.badgeCount > 0"
                :value="sidebarLink.badgeCount"
                severity="danger"
              />

              <RiArrowRightSLine
                v-if="
                  isLinkActive(sidebarLink) &&
                  isExpanded &&
                  !(sidebarLink.badgeCount > 0)
                "
                class="text-surface-600"
              />
            </button>
          </li>
        </ul>
      </div>

      <div class="mt-auto p-4">
        <BaseDropdown v-slot="{ toggle, id }" :menu-items="userMenuItems">
          <Button
            @click="toggle"
            size="large"
            severity="secondary"
            :variant="isExpanded ? 'outlined' : 'text'"
            class="w-full"
            aria-haspopup="true"
            :aria-controls="id"
            data-testid="user-menu"
          >
            <BaseAvatar
              v-if="user"
              :url="user.avatar"
              :name="user.name ?? 'N/A'"
              :surname="user.surname ?? 'N/A'"
              class="shrink-0"
            />
            <template v-if="isExpanded">
              <div class="grow text-left font-medium">
                <div class="line-clamp-1 text-sm text-surface-950">
                  {{ user.fullName }}
                </div>
                <div class="line-clamp-1 text-xs">{{ user.company.name }}</div>
              </div>
              <RiArrowDownSFill />
            </template>
          </Button>
        </BaseDropdown>
      </div>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { useCurrentUser } from '@/composables/useCurrentUser'
import BaseAvatar from '@/components/BaseAvatar.vue'
import BaseDropdown from '@/components/BaseDropdown.vue'
import RiLayoutGridLine from '~icons/ri/layout-grid-line'
import RiStackLine from '~icons/ri/stack-line'
import RiFoldersLine from '~icons/ri/folders-line'
import RiGroupLine from '~icons/ri/group-line'
import RiArrowRightSLine from '~icons/ri/arrow-right-s-line'
import RiArrowDownSFill from '~icons/ri/arrow-down-s-fill'
import RiBillLine from '~icons/ri/bill-line'
import RiRecycleLine from '~icons/ri/recycle-line'
import RiSuitcaseLine from '~icons/ri/suitcase-line'
import RiNotification3Line from '~icons/ri/notification-3-line'
import RiSidebarFoldLine from '~icons/ri/sidebar-fold-line'
import { computed, FunctionalComponent, SVGAttributes } from 'vue'
import { useI18n } from 'vue-i18n'
import { navigateTo } from '@/utils/navigate-to'
import { usePage, router } from '@inertiajs/vue3'
import { useUnreadNotificationsCount } from '@/modules/inbox/composables/useUnreadNotificationsCount'
import RiSettings3Line from '~icons/ri/settings-3-line'
import RiLogoutBoxRLine from '~icons/ri/logout-box-r-line'
import MusthadFullLogo from '@/assets/images/logo-full.svg'
import MusthadLogo from '@/assets/images/logo.svg'

const isExpanded = defineModel<boolean>({ required: true })

const { user, role } = useCurrentUser()
const { t } = useI18n()

type SidebarLink = {
  label: string
  icon: FunctionalComponent<SVGAttributes, {}, any, {}>
  link: string
  isHidden: boolean
  soon: boolean
  limited: boolean
  roles: string[]
  badgeCount: number
  activePrefixes?: string[]
}

const page = usePage()

const { unreadNotificationsCount } = useUnreadNotificationsCount()

const SIDEBAR_LINKS = computed<SidebarLink[]>(() =>
  [
    {
      label: t('global.sidebar.dashboard'),
      icon: RiLayoutGridLine,
      link: '/dashboard',
      isHidden: false,
      soon: false,
      limited: user.value.company.limitedAccess,
      roles: ['customers'],
      badgeCount: 0,
    },
    {
      label: t('global.sidebar.partners'),
      icon: RiGroupLine,
      link: '/partners',
      isHidden: false,
      soon: false,
      limited: false,
      roles: ['circular_operators', 'customers'],
      badgeCount: 0,
    },
    {
      label: t('global.sidebar.feedstock'),
      icon: RiStackLine,
      link: '/feedstock',
      isHidden: false,
      soon: false,
      limited: false,
      roles: ['circular_operators'],
      badgeCount: 0,
    },
    {
      label: t('global.sidebar.processes'),
      icon: RiRecycleLine,
      link: '/processes',
      isHidden: false,
      soon: false,
      limited: false,
      roles: ['circular_operators'],
      badgeCount: 0,
      activePrefixes: ['/processes', '/process/'],
    },
    {
      label: t('global.sidebar.orders'),
      icon: RiFoldersLine,
      link: '/orders',
      isHidden: false,
      soon: false,
      limited: false,
      roles: ['circular_operators'],
      badgeCount: 0,
    },
    {
      label: t('global.sidebar.excess-inventory'),
      icon: RiStackLine,
      link: '/excess-inventory',
      isHidden: false,
      soon: false,
      limited: false,
      roles: ['customers'],
      badgeCount: 0,
    },
    {
      label: t('global.sidebar.circular-solutions'),
      icon: RiRecycleLine,
      link: '/search',
      isHidden: true,
      soon: false,
      limited: false,
      roles: ['circular_operators'],
      badgeCount: 0,
    },
    {
      label: t('global.sidebar.projects'),
      icon: RiFoldersLine,
      link: '/projects',
      isHidden: false,
      soon: false,
      limited: user.value.company.limitedAccess,
      roles: ['customers'],
      badgeCount: 0,
    },
    {
      label: t('global.sidebar.inbox'),
      icon: RiNotification3Line,
      link: '/inbox',
      isHidden: false,
      soon: false,
      limited: false,
      roles: ['circular_operators', 'customers'],
      badgeCount: unreadNotificationsCount.value,
    },
    // {
    //   label: t('global.sidebar.reporting'),
    //   icon: RiBillLine,
    //   link: '/reporting',
    //   isHidden: false,
    //   soon: true,
    //   limited: false,
    //   roles: ['customers'],
    // },
    {
      label: t('global.sidebar.company-profile'),
      icon: RiSuitcaseLine,
      link: '/company-profile',
      isHidden: false,
      soon: false,
      limited: false,
      roles: ['circular_operators', 'customers'],
      badgeCount: 0,
    },
  ]
    .filter((link) => !link.isHidden)
    .filter((link) => {
      if (link.roles.includes(role.value)) {
        return true
      }
      return false
    }),
)

const isLinkActive = (sidebarLink: SidebarLink) => {
  const activePrefixes = sidebarLink.activePrefixes ?? [sidebarLink.link]

  return activePrefixes.some((prefix) => page.url.startsWith(prefix))
}

const userMenuItems = computed(() => [
  {
    label: t('global.user-menu.settings'),
    icon: RiSettings3Line,
    command: () => {
      navigateTo('/settings')
    },
  },
  {
    label: t('global.user-menu.logout'),
    icon: RiLogoutBoxRLine,
    command: () => {
      router.post('/logout')
    },
  },
])

const getNavigationLinkTooltip = (link: SidebarLink) => {
  if (isExpanded.value) {
    return null
  } else {
    if (link.soon) {
      return `${link.label} (${t('global.soon')})`
    }
    if (link.limited) {
      return `${link.label} (${t('global.limited-access')})`
    }
    return null
  }
}
</script>
